import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigation } from "../../../hooks";
import { termsService } from "../../../services";
import { TERMS_URLS } from "../../../utils";
import LoadingSpinner from "../../common/LoadingSpinner";
import Icon from "../../ui/Icon";

interface ConsentState {
  marketingInfoCollectionAgreement: boolean;
  marketingInfoReceiptAgreement: boolean;
  marketingInfoCollectionAgreedAt: string | null;
}

const MarketingConsentPage: React.FC = () => {
  const { goBack } = useNavigation();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    marketingInfoCollectionAgreement: false,
    marketingInfoReceiptAgreement: false,
    marketingInfoCollectionAgreedAt: null,
  });

  useEffect(() => {
    const fetchConsentStatus = async () => {
      try {
        setLoading(true);
        const consentData = await termsService.getMarketingConsent();
        setConsent({
          marketingInfoCollectionAgreement:
            consentData.marketingInfoCollectionAgreement,
          marketingInfoReceiptAgreement:
            consentData.marketingInfoReceiptAgreement,
          marketingInfoCollectionAgreedAt:
            consentData.marketingInfoCollectionAgreedAt,
        });
      } catch (error) {
        console.error("마케팅 동의 상태 조회 실패:", error);
        toast.error("마케팅 동의 상태를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchConsentStatus();
  }, []);

  const handleToggle = async (
    field: "marketingInfoCollectionAgreement" | "marketingInfoReceiptAgreement"
  ) => {
    if (updating) return;

    const newValue = !consent[field];
    const newConsent = {
      ...consent,
      [field]: newValue,
    };

    try {
      setUpdating(true);
      const response = await termsService.updateMarketingConsent({
        marketingInfoCollectionAgreement:
          newConsent.marketingInfoCollectionAgreement,
        marketingInfoReceiptAgreement:
          newConsent.marketingInfoReceiptAgreement,
      });

      setConsent({
        marketingInfoCollectionAgreement:
          response.marketingInfoCollectionAgreement,
        marketingInfoReceiptAgreement: response.marketingInfoReceiptAgreement,
        marketingInfoCollectionAgreedAt: response.marketingInfoCollectionAgreedAt,
      });

      toast.success("마케팅 동의 상태가 변경되었습니다.");
    } catch (error) {
      console.error("마케팅 동의 상태 업데이트 실패:", error);
      toast.error("마케팅 동의 상태 변경에 실패했습니다.");
    } finally {
      setUpdating(false);
    }
  };

  const openTermsUrl = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formatConsentDate = (dateString: string | null): string | null => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}년 ${month}월 ${day}일`;
    } catch {
      return null;
    }
  };

  const consentDateText = formatConsentDate(consent.marketingInfoCollectionAgreedAt);

  if (loading) {
    return (
      <div className="h-screen-dvh bg-white flex flex-col">
        <header className="bg-white px-4 py-3 flex items-center">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="chevron-left" className="text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800">
            마케팅 정보 수신 동의
          </h1>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  return (
    <div className="h-screen-dvh bg-white flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center">
        <button
          onClick={goBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevron-left" className="text-gray-600" />
        </button>      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 bg-white">
        {/* 마케팅 정보 수집 및 이용 동의 */}
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <span className="text-base font-medium text-gray-900">
              마케팅 정보 수집 및 이용 동의
            </span>
            <button
              onClick={() => handleToggle("marketingInfoCollectionAgreement")}
              disabled={updating}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                consent.marketingInfoCollectionAgreement
                  ? "bg-emerald-400"
                  : "bg-gray-200"
              } ${updating ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  consent.marketingInfoCollectionAgreement
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
          </div>
          <button
            onClick={() => openTermsUrl(TERMS_URLS.MARKETING_CONSENT)}
            className="mt-1 text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center"
          >
            약관 보기
            <Icon name="chevronRight" size="xs" className="ml-0.5" />
          </button>
        </div>

        {/* 마케팅 정보 수신 동의 */}
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <span className="text-base font-medium text-gray-900">
              마케팅 정보 수신 동의
            </span>
            <button
              onClick={() => handleToggle("marketingInfoReceiptAgreement")}
              disabled={updating}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                consent.marketingInfoReceiptAgreement
                  ? "bg-emerald-400"
                  : "bg-gray-200"
              } ${updating ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <span
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  consent.marketingInfoReceiptAgreement
                    ? "translate-x-5"
                    : "translate-x-0"
                }`}
              />
            </button>
          </div>
          <button
            onClick={() => openTermsUrl(TERMS_URLS.MARKETING_INFO_RECEPTION)}
            className="mt-1 text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center"
          >
            약관 보기
            <Icon name="chevronRight" size="xs" className="ml-0.5" />
          </button>
        </div>

        {/* 구분선 */}
        <div className="h-2 bg-gray-100" />

        {/* 동의 날짜 정보 - 두 항목 모두 동의한 경우에만 표시 */}
        {consentDateText &&
          (consent.marketingInfoCollectionAgreement === true) && (
            <div className="px-4 py-4">
              <p className="text-sm text-gray-500">
                {consentDateText} 마케팅 정보 수신 및 이용에 동의했습니다.
              </p>
            </div>
          )}
      </main>
    </div>
  );
};

export default MarketingConsentPage;

